import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Card,
  Grid,
  Chip,
  Divider,
  Button,
} from '@mui/material';
import { ArrowBack, PersonAdd } from '@mui/icons-material';
import { donationsAPI } from '../services/api';
import { colors } from '../theme/modernTheme';
import { showSuccess, showError } from '../utils/toast';
import { formatDateForDisplay } from '../utils/dateUtils';
import DonationPledgeForm from '../components/DonationPledgeForm';
import DeleteConfirmationDialog from '../components/DeleteConfirmationDialog';
import { useAuth } from '../context/AuthContext';
import { getFrequencyLabel } from '../utils/frequencyLabel';

export default function DonationPledgePage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const formRef = React.useRef(null);

  const initialMode = id && id !== 'new' && searchParams.get('mode') !== 'edit' ? 'view' : 'edit';
  const [mode, setMode] = useState(initialMode);
  const [pledge, setPledge] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const canEdit = user?.role === 'ADMIN' || user?.role === 'STAFF';
  const canDelete = user?.role === 'ADMIN';
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';

  const handleAddContact = () => {
    // Call the form's handleAddContact method via ref
    if (formRef.current && formRef.current.handleAddContact) {
      formRef.current.handleAddContact();
    }
  };

  useEffect(() => {
    if (id && id !== 'new') {
      // Always load pledge data when id changes, regardless of mode
      // The form will use this data if in edit mode
      loadPledge();
    }
  }, [id]);

  const loadPledge = async () => {
    try {
      setLoading(true);
      const response = await donationsAPI.getOne(id);
      setPledge(response.data);
    } catch (error) {
      showError('Failed to load pledge');
      navigate('/donations');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = () => {
    setMode('edit');
    navigate(`/donations/${id}?mode=edit`, { replace: true });
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleting(true);

      await donationsAPI.delete(id);
      showSuccess('Pledge deleted successfully. Associated transactions have been preserved.');
      navigate('/donations');
    } catch (error) {
      showError(error?.error?.message || 'Failed to delete pledge');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleSuccess = () => {
    navigate('/donations');
  };

  const handleCancel = () => {
    if (id && id !== 'new') {
      setMode('view');
      navigate(`/donations/${id}`, { replace: true });
      loadPledge();
    } else {
      navigate('/donations');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return colors.success?.main || '#4caf50';
      case 'PAUSED':
        return colors.warning?.main || '#ff9800';
      case 'CLOSED':
        return colors.text.disabled;
      default:
        return colors.text.secondary;
    }
  };

  const getStatusBgColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return `${colors.success?.main || '#4caf50'}15`;
      case 'PAUSED':
        return `${colors.warning?.main || '#ff9800'}15`;
      case 'CLOSED':
        return `${colors.text.disabled}15`;
      default:
        return colors.action.hover;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <IconButton
            onClick={() => navigate('/donations')}
            size="small"
            sx={{
              '&:hover': { color: colors.primary.main },
            }}
          >
            <ArrowBack fontSize="small" />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              fontSize: '1.2rem',
              fontWeight: 700,
              color: colors.text.primary,
            }}
          >
            {isViewMode
              ? 'Pledge Details'
              : id && id !== 'new'
                ? 'Edit Pledge'
                : 'Create New Pledge'}
          </Typography>
        </Box>

        {isViewMode ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {canEdit && (
              <Button
                variant="text"
                size="small"
                onClick={handleEditClick}
                sx={{
                  textTransform: 'none',
                  borderRadius: '4px',
                  border: `1px solid ${colors.primary.main}`,
                  color: colors.primary.main,
                  '&:hover': {
                    textDecoration: 'none',
                    backgroundColor: `${colors.action.hover} !important`,
                  },
                }}
              >
                Edit
              </Button>
            )}
            {canDelete && (
              <Button
                variant="text"
                size="small"
                onClick={handleDeleteClick}
                sx={{
                  textTransform: 'none',
                  borderRadius: '4px',
                  border: `1px solid ${colors.error.main}`,
                  color: colors.error.main,
                  '&:hover': {
                    textDecoration: 'none',
                    backgroundColor: `${colors.action.error} !important`,
                  },
                }}
              >
                Delete
              </Button>
            )}
          </Box>
        ) : (
          isEditMode &&
          (id === 'new' || !id) && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<PersonAdd />}
              onClick={handleAddContact}
              sx={{
                borderRadius: '4px',
                textTransform: 'none',
                borderColor: colors.primary.main,
                color: colors.primary.main,
                '&:hover': {
                  borderColor: colors.primary.dark,
                  backgroundColor: `${colors.primary.main}10`,
                },
              }}
            >
              Add New Contact
            </Button>
          )
        )}
      </Box>

      {/* Content */}
      {isEditMode ? (
        <DonationPledgeForm
          ref={formRef}
          pledgeId={id !== 'new' ? id : null}
          pledgeData={pledge}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      ) : (
        <Grid container spacing={3}>
          {/* Donor Information Card */}
          <Grid item xs={12}>
            <Card
              sx={{
                borderRadius: '4px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                p: 3,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: colors.primary.main,
                  mb: 2,
                  pb: 1,
                  borderBottom: `2px solid ${colors.primary.main}`,
                }}
              >
                Donor Information
              </Typography>

              <Grid container spacing={3}>
                {/* Left Column */}
                <Grid item xs={12} md={6}>
                  <Box display="flex" flexDirection="column" gap={2}>
                    {/* Donor Name */}
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.text.secondary,
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Donor Name
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: colors.text.primary,
                          fontWeight: 500,
                          mt: 0.5,
                        }}
                      >
                        {pledge?.donor_name || '—'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Right Column */}
                <Grid item xs={12} md={6}>
                  <Box display="flex" flexDirection="column" gap={2}>
                    {/* Email */}
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.text.secondary,
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Email
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: colors.text.primary,
                          fontWeight: 500,
                          mt: 0.5,
                        }}
                      >
                        {pledge?.primary_email || '—'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Grid>

          {/* Pledge Information Card */}
          <Grid item xs={12}>
            <Card
              sx={{
                borderRadius: '4px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                p: 3,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: colors.primary.main,
                  mb: 2,
                  pb: 1,
                  borderBottom: `2px solid ${colors.primary.main}`,
                }}
              >
                Pledge Information
              </Typography>

              <Grid container spacing={3}>
                {/* Left Column - Amount, Collected Amount, Remaining Amount (Vertical) */}
                <Grid item xs={12} md={6}>
                  <Box display="flex" flexDirection="column" gap={2}>
                    {/* Amount */}
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.text.secondary,
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Amount
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          color: colors.text.primary,
                          fontWeight: 700,
                          mt: 0.5,
                        }}
                      >
                        {pledge?.currency === 'INR' ? '₹' : '$'}
                        {parseFloat(pledge?.amount || 0).toLocaleString()}
                      </Typography>
                    </Box>

                    {/* Collected Amount */}
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.text.secondary,
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Collected Amount
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          color: colors.text.primary,
                          fontWeight: 700,
                          mt: 0.5,
                        }}
                      >
                        {pledge?.currency === 'INR' ? '₹' : '$'}
                        {parseFloat(pledge?.collected_amount || 0).toLocaleString()}
                      </Typography>
                    </Box>

                    {/* Remaining Amount */}
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.text.secondary,
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Remaining Amount
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          color: colors.text.primary,
                          fontWeight: 700,
                          mt: 0.5,
                        }}
                      >
                        {pledge?.currency === 'INR' ? '₹' : '$'}
                        {parseFloat(
                          (pledge?.amount || 0) - (pledge?.collected_amount || 0),
                        ).toLocaleString()}
                      </Typography>
                    </Box>
                    {/* Remaining Amount */}
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.text.secondary,
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Trust
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          color: colors.text.primary,
                          fontWeight: 700,
                          mt: 0.5,
                        }}
                      >
                        {pledge?.trust_name || ''}
                      </Typography>
                    </Box>

                    {/* Purpose */}
                    {pledge?.purpose && (
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.text.secondary,
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            fontSize: '0.7rem',
                            letterSpacing: '0.5px',
                          }}
                        >
                          Purpose
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: colors.text.primary,
                            mt: 0.5,
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {pledge.purpose}
                        </Typography>
                      </Box>
                    )}

                    {/* Tags */}
                    {pledge?.tags && pledge.tags.length > 0 && (
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.text.secondary,
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            fontSize: '0.7rem',
                            letterSpacing: '0.5px',
                          }}
                        >
                          {pledge.tags.length === 1 ? 'Tag' : 'Tags'}
                        </Typography>
                        <Box
                          sx={{
                            mt: 0.5,
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.5,
                          }}
                        >
                          {pledge.tags.map((tag) => (
                            <Chip
                              key={tag.id}
                              label={tag.name}
                              size="small"
                              sx={{
                                backgroundColor: tag.color || 'rgba(139, 95, 191, 0.1)',
                                color: tag.color ? '#fff' : '#8B5FBF',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                height: '24px',
                                borderRadius: '4px',
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Grid>

                {/* Right Column - Frequency and Donation Schema */}
                <Grid item xs={12} md={6}>
                  <Box display="flex" flexDirection="column" gap={2}>
                    {/* Frequency */}
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.text.secondary,
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Frequency
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: colors.text.primary,
                          fontWeight: 500,
                          mt: 0.5,
                        }}
                      >
                        {getFrequencyLabel(pledge?.frequency)}
                      </Typography>
                    </Box>

                    {/* Donation Scheme */}
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.text.secondary,
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Donation Scheme
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: colors.text.primary,
                          fontWeight: 500,
                          mt: 0.5,
                        }}
                      >
                        {pledge?.donation_schema_name || '—'}
                      </Typography>
                    </Box>

                    {/* Status */}
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.text.secondary,
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Status
                      </Typography>
                      <Box mt={0.5}>
                        <Chip
                          label={pledge?.pledge_status || 'PENDING'}
                          size="small"
                          sx={{
                            backgroundColor:
                              pledge?.pledge_status === 'PENDING'
                                ? `#E3F2FD`
                                : pledge?.pledge_status === 'PAUSED'
                                  ? `#FEF3C7`
                                  : pledge?.pledge_status === 'CLOSED'
                                    ? `${colors.text.disabled}15`
                                    : colors.action.hover,
                            color:
                              pledge?.pledge_status === 'PENDING'
                                ? '#1976D2'
                                : pledge?.pledge_status === 'PAUSED'
                                  ? '#92400E'
                                  : pledge?.pledge_status === 'CLOSED'
                                    ? colors.text.disabled
                                    : colors.text.secondary,
                            fontWeight: 500,
                            fontSize: '0.75rem',
                            borderRadius: '4px',
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Pledge Start Date */}
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.text.secondary,
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Pledge Start Date
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: colors.text.primary,
                          fontWeight: 500,
                          mt: 0.5,
                        }}
                      >
                        {pledge?.pledge_start_date
                          ? formatDateForDisplay(pledge.pledge_start_date)
                          : '—'}
                      </Typography>
                    </Box>

                    {/* Pledge End Date */}
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.text.secondary,
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Pledge End Date
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: colors.text.primary,
                          fontWeight: 500,
                          mt: 0.5,
                        }}
                      >
                        {pledge?.pledge_end_date
                          ? formatDateForDisplay(pledge.pledge_end_date)
                          : 'Ongoing'}
                      </Typography>
                    </Box>

                    {/* Next Pledge Date - Only show for recurring donations */}
                    {!!pledge?.is_recurring && !!pledge?.next_pledge_date && (
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.text.secondary,
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            fontSize: '0.7rem',
                            letterSpacing: '0.5px',
                          }}
                        >
                          Upcoming Reminder Date
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: colors.primary.main,
                            fontWeight: 600,
                            mt: 0.5,
                          }}
                        >
                          {formatDateForDisplay(pledge.next_pledge_date)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Grid>

          {/* Metadata Card */}
          <Grid item xs={12}>
            <Card
              sx={{
                borderRadius: '4px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                p: 3,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: colors.primary.main,
                  mb: 2,
                  pb: 1,
                  borderBottom: `2px solid ${colors.primary.main}`,
                }}
              >
                Metadata
              </Typography>

              <Grid container spacing={3}>
                {/* Left Column */}
                <Grid item xs={12} md={6}>
                  <Box display="flex" flexDirection="column" gap={2}>
                    {/* Recorded By */}
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.text.secondary,
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Recorded By
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: colors.text.primary,
                          fontWeight: 500,
                          mt: 0.5,
                        }}
                      >
                        {pledge?.recorded_by_name || '—'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Right Column */}
                <Grid item xs={12} md={6}>
                  <Box display="flex" flexDirection="column" gap={2}>
                    {/* Created At */}
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.text.secondary,
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Created At
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: colors.text.primary,
                          fontWeight: 500,
                          mt: 0.5,
                        }}
                      >
                        {pledge?.created_at ? new Date(pledge.created_at).toLocaleString() : '—'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        itemName="pledge"
        deleting={deleting}
      >
        <Typography
          variant="body2"
          sx={{ color: colors.text.secondary, px: 2, textAlign: 'center' }}
        >
          This action cannot be undone. The pledge and any associated reminders will be permanently
          deleted. Associated transactions will be preserved.
        </Typography>
      </DeleteConfirmationDialog>
    </Box>
  );
}
